import {
  AlertTriangle,
  Bell,
  Bold,
  CheckCircle2,
  GitBranch,
  Inbox,
  Italic,
  MoreHorizontal,
  Plus,
  Rocket,
  RotateCw,
  Settings,
  Terminal,
  Trash2,
  Underline,
  XCircle,
} from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "../components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { EmptyState } from "../components/ui/empty-state";
import { ErrorState } from "../components/ui/error-state";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/ui/form";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../components/ui/hover-card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ModeToggle } from "../components/ui/mode-toggle";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../components/ui/pagination";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Progress } from "../components/ui/progress";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import { Skeleton } from "../components/ui/skeleton";
import { Slider } from "../components/ui/slider";
import { StatusBadge } from "../components/ui/status-badge";
import { StatusDot } from "../components/ui/status-dot";
import { Switch } from "../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { ThemeSelector } from "../components/ui/theme-selector";
import { Toggle } from "../components/ui/toggle";
import { ToggleGroup, ToggleGroupItem } from "../components/ui/toggle-group";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { useToast } from "../components/ui/use-toast";
import { cn } from "../lib/utils";

/* ================================================================== */
/* Layout helpers                                                      */
/* ================================================================== */

const GROUPS: { id: string; label: string }[] = [
  { id: "foundations", label: "Foundations" },
  { id: "actions", label: "Actions" },
  { id: "forms", label: "Forms & Inputs" },
  { id: "data", label: "Data Display" },
  { id: "overlays", label: "Overlays & Dialogs" },
  { id: "navigation", label: "Navigation" },
  { id: "feedback", label: "Feedback" },
];

function ModuleGroup({
  id,
  title,
  description,
  children,
}: {
  id: string;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24 space-y-8">
      <div className="space-y-1 border-l-4 border-primary pl-4">
        <h2 className="text-xl font-bold uppercase tracking-wide">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  );
}

function Block({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-0.5">
        <h3 className="text-base font-semibold">{title}</h3>
        {description ? (
          <p className="text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      <div className="rounded-lg bg-card/40 p-6">{children}</div>
    </div>
  );
}

/* ================================================================== */
/* Token data                                                          */
/* ================================================================== */

const SEMANTIC_COLORS: { name: string; bg: string; fg: string }[] = [
  { name: "background", bg: "bg-background", fg: "text-foreground" },
  { name: "card", bg: "bg-card", fg: "text-card-foreground" },
  { name: "popover", bg: "bg-popover", fg: "text-popover-foreground" },
  { name: "primary", bg: "bg-primary", fg: "text-primary-foreground" },
  { name: "secondary", bg: "bg-secondary", fg: "text-secondary-foreground" },
  { name: "muted", bg: "bg-muted", fg: "text-muted-foreground" },
  { name: "accent", bg: "bg-accent", fg: "text-accent-foreground" },
  {
    name: "destructive",
    bg: "bg-destructive",
    fg: "text-destructive-foreground",
  },
  { name: "success", bg: "bg-success", fg: "text-success-foreground" },
  { name: "warning", bg: "bg-warning", fg: "text-warning-foreground" },
  { name: "info", bg: "bg-info", fg: "text-info-foreground" },
];

const BRAND_SCALE = [
  "bg-avahana-50",
  "bg-avahana-100",
  "bg-avahana-200",
  "bg-avahana-300",
  "bg-avahana-400",
  "bg-avahana-500",
  "bg-avahana-600",
  "bg-avahana-700",
  "bg-avahana-800",
  "bg-avahana-900",
];

const TYPE_SCALE: { label: string; cls: string }[] = [
  { label: "Page title", cls: "text-3xl md:text-4xl font-bold tracking-tight" },
  { label: "Section title", cls: "text-2xl font-semibold tracking-tight" },
  { label: "Subsection", cls: "text-lg font-semibold" },
  { label: "Body (default)", cls: "text-sm" },
  { label: "Body large / prose", cls: "text-base" },
  { label: "Caption / secondary", cls: "text-sm text-muted-foreground" },
  { label: "Micro / label", cls: "text-xs font-medium" },
];

const SPACING = [
  { token: "1", px: 4 },
  { token: "2", px: 8 },
  { token: "3", px: 12 },
  { token: "4", px: 16 },
  { token: "6", px: 24 },
  { token: "8", px: 32 },
  { token: "12", px: 48 },
  { token: "16", px: 64 },
];

const RADII = [
  { cls: "rounded-sm", label: "sm" },
  { cls: "rounded-md", label: "md" },
  { cls: "rounded-lg", label: "lg" },
  { cls: "rounded-full", label: "full" },
];

const SHADOWS = ["shadow-sm", "shadow", "shadow-md", "shadow-lg"];

const BUTTON_VARIANTS = [
  "default",
  "secondary",
  "outline",
  "ghost",
  "link",
  "destructive",
] as const;

const ENTRANCE_ANIMATIONS = [
  "animate-fade-in",
  "animate-fade-in-up",
  "animate-fade-in-down",
  "animate-scale-in",
  "animate-slide-in-right",
];

/* ================================================================== */
/* Page                                                                */
/* ================================================================== */

export default function DesignSystem() {
  const { toast } = useToast();
  const [replayKey, setReplayKey] = React.useState(0);
  const form = useForm<{ name: string }>({ defaultValues: { name: "" } });

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Sticky group nav */}
      <div className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 max-w-6xl items-center gap-4 overflow-x-auto">
          <span className="font-semibold">Avahana UI</span>
          <nav className="flex items-center gap-1 text-sm">
            {GROUPS.map((g) => (
              <a
                key={g.id}
                href={`#${g.id}`}
                className="whitespace-nowrap rounded-md px-2.5 py-1 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                {g.label}
              </a>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <ThemeSelector />
            <ModeToggle />
          </div>
        </div>
      </div>

      <div className="container max-w-6xl space-y-16 py-10">
        {/* Intro */}
        <header className="space-y-2">
          <Badge variant="secondary">Design System</Badge>
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
            Components & Standards
          </h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            The living reference for theme tokens, motion and the shadcn/ui
            component library. Switch the accent theme and light/dark above to
            verify every token. See <code>DESIGN_SYSTEM.md</code> for the
            written standards.
          </p>
        </header>

        {/* ============================== FOUNDATIONS ============================== */}
        <ModuleGroup
          id="foundations"
          title="Foundations"
          description="Tokens that everything else is built from — color, type, spacing, radius and motion."
        >
          <Block
            title="Color tokens"
            description="Semantic tokens — always reference these, never raw hex. Driven by CSS variables in src/index.css."
          >
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {SEMANTIC_COLORS.map((c) => (
                <div
                  key={c.name}
                  className="overflow-hidden rounded-lg bg-card"
                >
                  <div
                    className={cn(
                      "flex h-20 items-end p-2 text-xs font-medium",
                      c.bg,
                      c.fg
                    )}
                  >
                    Aa
                  </div>
                  <div className="px-3 py-2 text-xs">
                    <div className="font-medium">{c.name}</div>
                    <code className="text-muted-foreground">{c.bg}</code>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 space-y-2">
              <h4 className="text-sm font-semibold">Brand scale — avahana</h4>
              <div className="flex overflow-hidden rounded-lg bg-card">
                {BRAND_SCALE.map((b) => (
                  <div key={b} className={cn("h-12 flex-1", b)} />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                The <code>primary</code> token re-skins with the selected accent
                theme; this fixed brand scale is for marketing surfaces.
              </p>
            </div>
          </Block>

          <Block
            title="Typography"
            description="System font stack with Tailwind's type scale."
          >
            <div className="space-y-4">
              {TYPE_SCALE.map((t) => (
                <div
                  key={t.label}
                  className="flex flex-col gap-1 border-b pb-4 last:border-0 sm:flex-row sm:items-baseline sm:justify-between"
                >
                  <span className={t.cls}>The quick brown fox</span>
                  <div className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {t.label}
                    </span>{" "}
                    · <code>{t.cls}</code>
                  </div>
                </div>
              ))}
            </div>
          </Block>

          <div className="grid gap-8 lg:grid-cols-2">
            <Block
              title="Spacing"
              description="4px base scale for padding, margin and gap."
            >
              <div className="space-y-2">
                {SPACING.map((s) => (
                  <div key={s.token} className="flex items-center gap-4">
                    <code className="w-10 shrink-0 text-xs text-muted-foreground">
                      {s.token}
                    </code>
                    <div
                      className="h-4 rounded-sm bg-primary"
                      style={{ width: s.px }}
                    />
                    <span className="text-xs text-muted-foreground">
                      {s.px}px
                    </span>
                  </div>
                ))}
              </div>
            </Block>

            <Block
              title="Radius & elevation"
              description="Radius from --radius (0.65rem). Keep elevation flat."
            >
              <div className="flex flex-wrap gap-6">
                {RADII.map((r) => (
                  <div key={r.cls} className="space-y-2 text-center">
                    <div className={cn("h-16 w-16 border bg-card", r.cls)} />
                    <code className="text-xs text-muted-foreground">
                      {r.label}
                    </code>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-6">
                {SHADOWS.map((s) => (
                  <div key={s} className="space-y-2 text-center">
                    <div className={cn("h-16 w-16 rounded-lg bg-card", s)} />
                    <code className="text-xs text-muted-foreground">{s}</code>
                  </div>
                ))}
              </div>
            </Block>
          </div>

          <Block
            title="Motion"
            description="Standard durations (fast 150ms · base 200ms · slow 300ms) and the emphasized easing. Honors prefers-reduced-motion."
          >
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">Entrance animations</h4>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setReplayKey((k) => k + 1)}
              >
                <RotateCw className="mr-2 h-4 w-4" />
                Replay
              </Button>
            </div>
            <div
              key={replayKey}
              className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5"
            >
              {ENTRANCE_ANIMATIONS.map((a) => (
                <div
                  key={a}
                  className={cn(
                    "flex h-20 items-center justify-center rounded-lg bg-card p-2 text-center text-2xs font-medium text-muted-foreground",
                    a
                  )}
                >
                  {a.replace("animate-", "")}
                </div>
              ))}
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Hover transition</h4>
                <div className="h-16 cursor-pointer rounded-lg bg-primary/70 transition-colors duration-slow hover:bg-primary" />
                <code className="text-xs text-muted-foreground">
                  transition-colors duration-slow
                </code>
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Shimmer (loading)</h4>
                <div className="relative h-16 overflow-hidden rounded-lg bg-muted">
                  <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-foreground/10 to-transparent" />
                </div>
                <code className="text-xs text-muted-foreground">
                  animate-shimmer
                </code>
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Pulse (attention)</h4>
                <div className="flex h-16 items-center justify-center rounded-lg bg-card">
                  <span className="h-3 w-3 animate-pulse-subtle rounded-full bg-success" />
                </div>
                <code className="text-xs text-muted-foreground">
                  animate-pulse-subtle
                </code>
              </div>
            </div>
          </Block>
        </ModuleGroup>

        {/* ============================== ACTIONS ============================== */}
        <ModuleGroup
          id="actions"
          title="Actions"
          description="Buttons and pressable toggles. Select with the variant / size props — never override base styles."
        >
          <Block title="Buttons" description="Variants (rows) × sizes.">
            <div className="space-y-4">
              {BUTTON_VARIANTS.map((v) => (
                <div key={v} className="flex flex-wrap items-center gap-3">
                  <code className="w-24 shrink-0 text-xs text-muted-foreground">
                    {v}
                  </code>
                  <Button variant={v} size="sm">
                    Small
                  </Button>
                  <Button variant={v}>Default</Button>
                  <Button variant={v} size="lg">
                    Large
                  </Button>
                  <Button variant={v} disabled>
                    Disabled
                  </Button>
                </div>
              ))}
              <div className="flex items-center gap-3 pt-2">
                <code className="w-24 shrink-0 text-xs text-muted-foreground">
                  icon
                </code>
                <Button size="icon">
                  <Plus className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="outline">
                  <Settings className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Block>

          <Block
            title="Toggle & toggle group"
            description="Single pressable toggles and grouped multi/single-select."
          >
            <div className="flex flex-wrap items-center gap-8">
              <Toggle aria-label="Bold">
                <Bold className="h-4 w-4" />
              </Toggle>
              <ToggleGroup type="multiple" variant="outline">
                <ToggleGroupItem value="bold" aria-label="Bold">
                  <Bold className="h-4 w-4" />
                </ToggleGroupItem>
                <ToggleGroupItem value="italic" aria-label="Italic">
                  <Italic className="h-4 w-4" />
                </ToggleGroupItem>
                <ToggleGroupItem value="underline" aria-label="Underline">
                  <Underline className="h-4 w-4" />
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </Block>
        </ModuleGroup>

        {/* ============================== FORMS ============================== */}
        <ModuleGroup
          id="forms"
          title="Forms & Inputs"
          description="Field primitives and the Form wrapper (react-hook-form + zod)."
        >
          <div className="grid gap-8 lg:grid-cols-2">
            <Block title="Text fields">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ds-name">Project name</Label>
                  <Input id="ds-name" placeholder="avahana-api" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ds-desc">Description</Label>
                  <Textarea
                    id="ds-desc"
                    placeholder="What does this service do?"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ds-region">Region</Label>
                  <Select>
                    <SelectTrigger id="ds-region" className="w-full">
                      <SelectValue placeholder="Select a region" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="us-east">us-east</SelectItem>
                      <SelectItem value="us-west">us-west</SelectItem>
                      <SelectItem value="eu-central">eu-central</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Block>

            <Block title="Selection controls">
              <div className="space-y-5">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Checkbox id="ds-cb1" defaultChecked />
                    <Label htmlFor="ds-cb1">Auto-deploy on push</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox id="ds-cb2" />
                    <Label htmlFor="ds-cb2">Require approval</Label>
                  </div>
                </div>
                <Separator />
                <RadioGroup defaultValue="prod" className="gap-2">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="prod" id="ds-r1" />
                    <Label htmlFor="ds-r1">Production</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="staging" id="ds-r2" />
                    <Label htmlFor="ds-r2">Staging</Label>
                  </div>
                </RadioGroup>
                <Separator />
                <div className="flex items-center gap-2">
                  <Switch id="ds-sw" defaultChecked />
                  <Label htmlFor="ds-sw">Enable metrics</Label>
                </div>
                <div className="space-y-2">
                  <Label>Replica count</Label>
                  <Slider defaultValue={[3]} max={10} step={1} />
                </div>
              </div>
            </Block>
          </div>

          <Block
            title="Form (react-hook-form + zod)"
            description="FormField wires label, control, description and validation message to a controlled field."
          >
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(() =>
                  toast({ title: "Submitted", description: "Form is valid." })
                )}
                className="max-w-md space-y-4"
              >
                <FormField
                  control={form.control}
                  name="name"
                  rules={{ required: "A service name is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Service name</FormLabel>
                      <FormControl>
                        <Input placeholder="avahana-api" {...field} />
                      </FormControl>
                      <FormDescription>
                        Lowercase, hyphen-separated.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit">Create service</Button>
              </form>
            </Form>
          </Block>
        </ModuleGroup>

        {/* ============================== DATA DISPLAY ============================== */}
        <ModuleGroup
          id="data"
          title="Data Display"
          description="Surfaces for presenting state and records — badges, tables, cards, accordions and loaders."
        >
          <Block
            title="Badges & status"
            description="Static badges, plus status-driven badge/dot from lib/statusMeta."
          >
            <div className="flex flex-wrap items-center gap-2">
              <Badge>default</Badge>
              <Badge variant="secondary">secondary</Badge>
              <Badge variant="outline">outline</Badge>
              <Badge variant="destructive">destructive</Badge>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <StatusBadge kind="deployment" status="running" />
              <StatusBadge kind="deployment" status="deploying" />
              <StatusBadge kind="deployment" status="failed" />
              <StatusDot kind="service" status="active" />
              <StatusDot kind="service" status="error" />
            </div>
          </Block>

          <Block
            title="Table"
            description="Standard data table for services, deployments and builds."
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Service</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead className="text-right">Replicas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">avahana-api</TableCell>
                  <TableCell>
                    <StatusBadge kind="service" status="active" />
                  </TableCell>
                  <TableCell>us-east</TableCell>
                  <TableCell className="text-right">3</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">avahana-web</TableCell>
                  <TableCell>
                    <StatusBadge kind="deployment" status="deploying" />
                  </TableCell>
                  <TableCell>eu-central</TableCell>
                  <TableCell className="text-right">2</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">worker</TableCell>
                  <TableCell>
                    <StatusBadge kind="service" status="error" />
                  </TableCell>
                  <TableCell>us-east</TableCell>
                  <TableCell className="text-right">1</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Block>

          <div className="grid gap-8 lg:grid-cols-2">
            <Block title="Cards & loaders">
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Production cluster</CardTitle>
                    <CardDescription>
                      3 nodes · us-east · healthy
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex justify-between">
                      <span>Build progress</span>
                      <span className="font-medium text-foreground">66%</span>
                    </div>
                    <Progress value={66} />
                  </CardContent>
                  <CardFooter className="gap-2">
                    <Button size="sm">Manage</Button>
                    <Button size="sm" variant="outline">
                      Logs
                    </Button>
                  </CardFooter>
                </Card>
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback>AV</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                </div>
              </div>
            </Block>

            <Block
              title="Accordion"
              description="Disclosure for FAQs and advanced settings."
            >
              <Accordion type="single" collapsible>
                <AccordionItem value="region">
                  <AccordionTrigger>Region & networking</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    Choose a deployment region and configure private networking
                    between your services.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="scaling">
                  <AccordionTrigger>Autoscaling</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    Set min/max replicas and the CPU target that triggers
                    scaling.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Block>
          </div>

          <Block title="Pagination">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious href="#" />
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#">1</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#" isActive>
                    2
                  </PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#">3</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext href="#" />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </Block>
        </ModuleGroup>

        {/* ============================== OVERLAYS ============================== */}
        <ModuleGroup
          id="overlays"
          title="Overlays & Dialogs"
          description="Layered surfaces — modals, sheets, menus, popovers and the command palette."
        >
          <Block
            title="Dialog · Sheet · Confirm"
            description="Modal dialog, side sheet, and AlertDialog for destructive confirmations (replaces window.confirm)."
          >
            <div className="flex flex-wrap items-center gap-3">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline">Open dialog</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Invite a teammate</DialogTitle>
                    <DialogDescription>
                      They'll get access to this project's services and
                      deployments.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-2">
                    <Label htmlFor="ds-invite">Email</Label>
                    <Input id="ds-invite" placeholder="teammate@avahana.com" />
                  </div>
                  <DialogFooter>
                    <Button>Send invite</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline">Open sheet</Button>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>Service settings</SheetTitle>
                    <SheetDescription>
                      Slide-in panel for contextual settings and detail views.
                    </SheetDescription>
                  </SheetHeader>
                </SheetContent>
              </Sheet>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete project
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this project?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently removes the project, its services and all
                      deployment history. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </Block>

          <Block
            title="Menus & floating content"
            description="Dropdown menu, popover, hover card and tooltip."
          >
            <div className="flex flex-wrap items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <MoreHorizontal className="mr-2 h-4 w-4" />
                    Actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuLabel>Service</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>
                    <Rocket className="mr-2 h-4 w-4" />
                    Deploy
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <GitBranch className="mr-2 h-4 w-4" />
                    Create branch
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline">Open popover</Button>
                </PopoverTrigger>
                <PopoverContent>
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">Scale service</h4>
                    <p className="text-sm text-muted-foreground">
                      Adjust replicas and resource limits.
                    </p>
                  </div>
                </PopoverContent>
              </Popover>

              <HoverCard>
                <HoverCardTrigger asChild>
                  <Button variant="link">@avahana</Button>
                </HoverCardTrigger>
                <HoverCardContent>
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarFallback>AV</AvatarFallback>
                    </Avatar>
                    <div className="text-sm">
                      <p className="font-medium">Avahana Platform</p>
                      <p className="text-muted-foreground">
                        Digital independence
                      </p>
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline">Hover for tooltip</Button>
                  </TooltipTrigger>
                  <TooltipContent>Deploys to production</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </Block>

          <Block
            title="Command palette"
            description="cmdk-powered fuzzy search and keyboard navigation."
          >
            <Command className="max-w-md rounded-lg bg-card shadow-md">
              <CommandInput placeholder="Type a command or search…" />
              <CommandList>
                <CommandEmpty>No results found.</CommandEmpty>
                <CommandGroup heading="Actions">
                  <CommandItem>
                    <Rocket className="h-4 w-4" />
                    <span>Deploy service</span>
                    <CommandShortcut>⌘D</CommandShortcut>
                  </CommandItem>
                  <CommandItem>
                    <GitBranch className="h-4 w-4" />
                    <span>Create branch</span>
                  </CommandItem>
                  <CommandItem>
                    <Settings className="h-4 w-4" />
                    <span>Open settings</span>
                    <CommandShortcut>⌘,</CommandShortcut>
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </Block>
        </ModuleGroup>

        {/* ============================== NAVIGATION ============================== */}
        <ModuleGroup
          id="navigation"
          title="Navigation"
          description="Moving between views and showing location in a hierarchy."
        >
          <Block title="Tabs">
            <Tabs defaultValue="overview" className="w-full max-w-lg">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="metrics">Metrics</TabsTrigger>
                <TabsTrigger value="logs">Logs</TabsTrigger>
              </TabsList>
              <TabsContent
                value="overview"
                className="pt-4 text-sm text-muted-foreground"
              >
                Service summary, status and quick actions.
              </TabsContent>
              <TabsContent
                value="metrics"
                className="pt-4 text-sm text-muted-foreground"
              >
                CPU, memory and request charts.
              </TabsContent>
              <TabsContent
                value="logs"
                className="pt-4 text-sm text-muted-foreground"
              >
                Streaming application logs.
              </TabsContent>
            </Tabs>
          </Block>

          <Block title="Breadcrumb">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="#">Projects</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink href="#">avahana-api</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>Settings</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </Block>
        </ModuleGroup>

        {/* ============================== FEEDBACK ============================== */}
        <ModuleGroup
          id="feedback"
          title="Feedback"
          description="Communicating outcomes — inline alerts, transient toasts and full-surface states."
        >
          <Block
            title="Alerts"
            description="Inline messaging for the four tones."
          >
            <div className="space-y-3">
              <Alert>
                <Terminal className="h-4 w-4" />
                <AlertTitle>Heads up</AlertTitle>
                <AlertDescription>
                  Default alert for neutral, informational messages.
                </AlertDescription>
              </Alert>
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertTitle>Deployment failed</AlertTitle>
                <AlertDescription>
                  The build exited with a non-zero status.
                </AlertDescription>
              </Alert>
              <Alert className="border-success/50 text-success [&>svg]:text-success">
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>Deployed</AlertTitle>
                <AlertDescription>Service is live.</AlertDescription>
              </Alert>
              <Alert className="border-warning/50 text-warning [&>svg]:text-warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Degraded</AlertTitle>
                <AlertDescription>One replica is unhealthy.</AlertDescription>
              </Alert>
            </div>
          </Block>

          <Block
            title="Toast"
            description="Transient notifications via useToast(). The Toaster is mounted app-wide."
          >
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() =>
                  toast({
                    title: "Deployment started",
                    description: "avahana-api is rolling out to us-east.",
                  })
                }
              >
                <Bell className="mr-2 h-4 w-4" />
                Show toast
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  toast({
                    variant: "destructive",
                    title: "Deployment failed",
                    description: "Build exited with status 1.",
                  })
                }
              >
                Show error toast
              </Button>
            </div>
          </Block>

          <div className="grid gap-8 lg:grid-cols-2">
            <Block
              title="Empty state"
              description="For zero-data screens with a primary action."
            >
              <EmptyState
                icon={Inbox}
                title="No services yet"
                description="Create your first service to start deploying."
                primaryAction={{ label: "Create service", icon: Plus }}
              />
            </Block>
            <Block
              title="Error state"
              description="For failed loads with a retry affordance."
            >
              <ErrorState
                title="Couldn't load services"
                description="The request timed out. Check your connection."
                onRetry={() => toast({ title: "Retrying…" })}
              />
            </Block>
          </div>
        </ModuleGroup>
      </div>
    </div>
  );
}
